import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : transcription parts
// =============================================================================
// ---Third-party imports
import React, { useEffect, useState } from 'react';
import { Button, Transition, Image } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { mapItems, items, dict, flatten } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { WikiPane } from 'uix/wiki/render';
import { useWikiRendered } from 'uix/wiki/hooks';
import { useRouter, useSelector } from 'uix/hooks';
import { Link, XMenu } from 'uix/components';
// ---Local imports
import S from './components.css';
// ---Globals
export const TranscriptionWiki = ({ ident })=>{
    let wikidata = useWikiRendered({
        ident
    });
    return /*#__PURE__*/ _jsx(WikiPane, {
        rendered: wikidata?.rendered
    });
};
const NAV = {
    account: 'tabUserAccount',
    transcriptions: 'transcriptions',
    users: 'tabUserManage',
    transcriptionHowtos: 'transcriptionHowtos'
};
const NAVIcons = {
    account: 'user circle outline',
    transcriptions: 'transcript',
    users: 'users',
    transcriptionHowtos: 'help'
};
const SUBNAV = {
    transcriptions: {
        activity: 'lastModifications',
        review: 'transcriptionReview',
        begun: 'transcriptionsBegun',
        published: 'transcriptionsPublished'
    },
    transcriptionHowtos: {
        specs: 'transcriptionSpecs',
        guidelines: 'transcriptionGuidelines',
        trguidelines: 'transcriptionTrGuidelines',
        terms: 'termsOfUse'
    }
};
const DebugCompare = ({})=>{
    let { request } = useRouter();
    let apiurl = useSelector(Sel.apiurl);
    return /*#__PURE__*/ _jsx("a", {
        style: {
            float: 'right'
        },
        href: apiurl.href(`/transcript/${request.tabname}/${request.id || ''}`),
        target: "_compare",
        children: "[compare]"
    });
};
const NavRail = ({ onSubMenuChange, logoUrl })=>{
    let { request } = useRouter();
    let i18n = useSelector(Sel.i18n);
    // determine chosen subnav group
    let tabname2subnavname = dict(flatten(mapItems(SUBNAV, (subnavname, v)=>mapItems(v, (tabname)=>[
                tabname,
                subnavname
            ]))));
    let subnavname = tabname2subnavname[request.tabname];
    useEffect(()=>{
        subnavname ? onSubMenuChange(true) : onSubMenuChange(false);
    }, [
        subnavname
    ]);
    // build main nav
    let navitems = mapItems(NAV, (navname, caption)=>{
        let tabname = navname;
        let is_active = tabname == request.tabname;
        if (SUBNAV[navname]) {
            tabname = items(SUBNAV[navname])[0][0];
            is_active = navname == subnavname;
        }
        return /*#__PURE__*/ _jsx(XMenu.LinkItem, {
            icon: NAVIcons[navname],
            content: i18n.get(caption),
            tabname: tabname,
            active: is_active
        }, tabname);
    });
    // Erstelle die Subnav-Items, falls vorhanden
    let subnavItems = null;
    if (subnavname) {
        subnavItems = mapItems(SUBNAV[subnavname], (tabname, caption)=>{
            let isActive = tabname === request.tabname;
            return /*#__PURE__*/ _jsx(XMenu.LinkItem, {
                content: i18n.get(caption),
                tabname: tabname,
                active: isActive
            }, tabname);
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: S.menuContainer,
        children: [
            /*#__PURE__*/ _jsxs(XMenu, {
                className: S.navRail,
                vertical: true,
                borderless: true,
                padded: true,
                attached: true,
                size: "large",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: S.top,
                        children: logoUrl && /*#__PURE__*/ _jsx(XMenu.LinkItem, {
                            children: /*#__PURE__*/ _jsx(Image, {
                                src: logoUrl,
                                style: {
                                    height: '2.75rem'
                                }
                            })
                        }, "logo")
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: S.center,
                        children: [
                            navitems,
                            /*#__PURE__*/ _jsx(XMenu.LinkItem, {
                                icon: "discussions",
                                content: i18n.get('discussionBoard'),
                                // tabname={tabname}
                                path: 'https://e-manuscripta-forum.ch/'
                            }, 'discussionBoard')
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: S.bottom,
                        children: /*#__PURE__*/ _jsx(XMenu.Item, {
                            children: /*#__PURE__*/ _jsx(DebugCompare, {})
                        })
                    })
                ]
            }),
            subnavname && /*#__PURE__*/ _jsx(XMenu, {
                className: S.navDrawer,
                secondary: true,
                vertical: true,
                attached: true,
                children: subnavItems
            })
        ]
    });
};
export const TranscriptionLayout = ({ children })=>{
    let apiurl = useSelector(Sel.apiurl);
    let url = apiurl.href(`/favicon.ico`);
    //!TODO: make it depending on children rendertime
    let [show, setShow] = useState(false);
    let [hasSubMenu, setHasSubMenu] = useState(false);
    useEffect(()=>{
        setTimeout(()=>setShow(true), 550);
    }, [
        children
    ]);
    const handleSubMenuChange = (hasSubMenu)=>{
        setHasSubMenu(hasSubMenu);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: S.layout,
        children: [
            /*#__PURE__*/ _jsx(NavRail, {
                logoUrl: url,
                onSubMenuChange: handleSubMenuChange
            }),
            /*#__PURE__*/ _jsx(Transition, {
                visible: show,
                animation: "fade",
                duration: 300,
                children: /*#__PURE__*/ _jsx("div", {
                    className: clsx(S.content, hasSubMenu && S.hasDrawer),
                    children: children
                })
            })
        ]
    });
};
export const BasicButton = ({ link, ...props })=>{
    let button = /*#__PURE__*/ _jsx(Button, {
        ...props,
        basic: true,
        compact: true,
        size: "tiny"
    });
    if (link) {
        button = /*#__PURE__*/ _jsx(Link, {
            ...link,
            children: button
        });
    }
    return button;
};
export const UserInfo = ({ fullname, email, puid, showActivityButton = false })=>/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("span", {
                style: {
                    minWidth: '10em',
                    display: 'inline-block'
                },
                children: [
                    fullname || '[n/a]',
                    " "
                ]
            }),
            /*#__PURE__*/ _jsx("tt", {
                style: {
                    minWidth: '10em',
                    display: 'inline-block',
                    backgroundColor: '#CCC'
                },
                children: email || '[n/a]'
            }),
            showActivityButton && /*#__PURE__*/ _jsx(BasicButton, {
                floated: "right",
                icon: "cog",
                title: "Aktivität",
                style: {
                    marginRight: '20px'
                }
            })
        ]
    });
